import {
  ACCOUNT_TAB_KEYS,
  type AccountTabKey,
  defaultAccountHeaderCopy,
  formatAmountWithUnit,
  heldForRevealsLine,
  memberSinceLabel,
} from "@loot-os/api-contract";
import type { EndUser } from "@loot-os/client";
import type {
  AccountHeaderContent,
  AccountTabsContent,
} from "@loot-os/design-system/templates/account/accountContent";
import { useQuery } from "@tanstack/react-query";

import { spendableCents } from "../../../lib/balance.js";
import { useCurrency } from "../../../lib/currency.js";
import { setDepositOpen } from "../../../lib/deposit.js";
import { useProfile } from "../../../lib/profile.js";
import { lootMeQueryOptions } from "../../../lib/queries.js";
import { useBalance, useShopper } from "../../../lib/shopper.js";
import { useTenant } from "../../../lib/tenant.js";
import { useTerminology } from "../../../lib/terminology.js";
import { accountHref } from "../account/content/search.js";

/**
 * GET /v1/me, shared by the account's pages. Stores that require identity verification refetch
 * it on every return to the tab: the Veriff flow runs in another tab and its decision arrives
 * through a webhook, so this is how Verification moves from "pending" to "verified" without a
 * reload (boxed's shopper has to reload).
 */
export function useLootMe(): EndUser | null {
  const { sessionToken } = useShopper();
  const { config } = useTenant();
  return (
    useQuery({
      ...lootMeQueryOptions(),
      enabled: !!sessionToken,
      refetchOnWindowFocus: config.kycRequired ? "always" : undefined,
    }).data ?? null
  );
}

/**
 * The header over the account's pages: the shopper's name, the balance and Deposit, in the
 * store's default copy (`defaultAccountHeaderCopy`).
 */
export function useAccountHeaderContent(me: EndUser | null): AccountHeaderContent {
  const { user } = useShopper();
  const { hasFeature } = useTenant();
  const { showProfile } = useProfile();
  const currency = useCurrency();
  const terms = useTerminology();
  const copy = defaultAccountHeaderCopy(terms).header;
  const balance = useBalance().data ?? null;
  const username = me?.username ?? null;
  return {
    name: username ?? copy.unnamed,
    named: username !== null,
    avatarName: username ?? user?.email ?? copy.unnamed,
    detail: me ? copy.memberSince(memberSinceLabel(me.createdAt)) : undefined,
    profile: { label: copy.viewProfile, disabled: !me, onClick: () => me && showProfile(me.id) },
    balance: {
      label: copy.balance,
      cents: balance ? spendableCents(balance) : null,
      held:
        balance && balance.heldCents > 0
          ? heldForRevealsLine(formatAmountWithUnit(balance.heldCents, currency), terms)
          : undefined,
    },
    // Opens the Deposit dialog over the page, as the chrome's Deposit does.
    deposit: hasFeature("balancePurchases")
      ? { label: copy.deposit, onClick: () => setDepositOpen(true) }
      : null,
  };
}

/** The account's tabs: the inventory (the Vault, its own route), then the Account page's two. */
export function useAccountTabsContent(current: AccountTabKey): AccountTabsContent {
  const copy = defaultAccountHeaderCopy(useTerminology()).tabs;
  const hrefs: Record<AccountTabKey, string> = {
    vault: "/inventory",
    settings: accountHref("settings"),
    history: accountHref("history"),
  };
  return {
    label: copy.label,
    items: ACCOUNT_TAB_KEYS.map((id) => ({
      id,
      label: copy.items[id],
      href: hrefs[id],
      current: id === current,
    })),
  };
}
